/**
 * Centralized Branding Configuration
 *
 * Edit this single file to change ALL branding across the entire dashboard.
 * Colors, logos, and company name are all controlled from here.
 *
 * After editing, the entire app will reflect the new branding automatically.
 */

export interface BrandingConfig {
  company: {
    name: string;
    tagline?: string;
    copyright?: string;
  };
  logos: {
    primary: string;      // Main logo (e.g., "/logo.png")
    wordmark: string;     // Text/wordmark logo (e.g., "/wordmark.svg")
    favicon?: string;     // Optional favicon path
  };
  colors: {
    primary: string;      // Main brand color
    secondary: string;    // Secondary/gradient start color
    tertiary: string;     // Tertiary/gradient end color
    accent: string;       // Accent/highlight color
    ring: string;         // Focus ring color
    gradient: {
      start: string;
      end: string;
      angle: number;      // Gradient angle in degrees
    };
    chart: string[];      // Chart color palette (5+ colors recommended)
    // Accent colors for metric cards and UI variety
    accents?: {
      green: string;      // For revenue/growth metrics
      blue: string;       // For rates/percentages
      purple: string;     // For unique metrics
      teal: string;       // For alternative metrics
    };
    // Page background colors (light and dark mode)
    background?: {
      light: string;      // Background color for light mode (default: #f5f5f5)
      dark: string;       // Background color for dark mode (default: #0a0a0a)
    };
    // Card/panel background colors
    card?: {
      light: string;      // Card background for light mode (default: #ffffff)
      dark: string;       // Card background for dark mode (default: #171717)
    };
  };
  email: {
    headerGradientStart: string;
    headerGradientEnd: string;
    buttonBackground: string;
    accentText: string;
  };
}

/**
 * ============================================
 * EDIT BELOW TO CHANGE BRANDING
 * ============================================
 */
export const branding: BrandingConfig = {
  company: {
    name: "SalesGrid",
    tagline: "Sales Dashboard",
    copyright: "SalesGrid",
  },

  logos: {
    primary: "/logo.png",
    wordmark: "/logo.png",    // Using same logo (no separate wordmark)
    favicon: "/logo.png",
  },

  colors: {
    // Main brand colors - Vivid Orange theme (matching logo)
    primary: "#f04913",      // Vivid orange (logo color) - primary actions, active states
    secondary: "#ff6b3d",    // Lighter orange - gradient start
    tertiary: "#c73a0e",     // Dark orange - gradient end
    accent: "#ffb199",       // Peach/orange tint - highlights, badges
    ring: "#f04913",         // Vivid orange - focus rings

    // Gradient definition
    gradient: {
      start: "#ff6b3d",      // Lighter orange
      end: "#c73a0e",        // Dark orange
      angle: 135,            // Gradient angle (degrees)
    },

    // Chart color palette - Orange-focused for brand consistency
    chart: [
      "#f04913",  // Vivid orange (primary brand color)
      "#ff6b3d",  // Lighter orange
      "#c73a0e",  // Dark orange
      "#ff8a5b",  // Light coral orange
      "#e05a2b",  // Medium orange
      "#ff7043",  // Deep coral
      "#b83d14",  // Dark rust
      "#ff5722",  // Bright orange
    ],

    // Accent colors for metric cards and UI variety (complement the orange brand)
    accents: {
      green: "#10b981",   // Emerald green - for revenue/growth
      blue: "#0ea5e9",    // Sky blue - for rates/percentages
      purple: "#8b5cf6",  // Violet - for unique metrics
      teal: "#14b8a6",    // Teal - for alternative metrics
    },

    // Page background colors
    background: {
      light: "#f5f5f5",   // Light gray for light mode
      dark: "#0a0a0a",    // Near black for dark mode
    },

    // Card/panel background colors
    card: {
      light: "#ffffff",   // White for light mode
      dark: "#171717",    // Dark gray for dark mode
    },
  },

  email: {
    headerGradientStart: "#ff6b3d",
    headerGradientEnd: "#c73a0e",
    buttonBackground: "#f04913",
    accentText: "#f04913",
  },
};

// ============================================
// HELPER FUNCTIONS (do not edit)
// ============================================

/**
 * Generate CSS gradient string from config
 */
export function getGradientCSS(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}

/**
 * Generate Tailwind gradient classes
 * Returns: "from-[#11b2f8] to-[#504df9]"
 */
export function getGradientTailwind(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}

/**
 * Get chart color by index (wraps around if index exceeds array length)
 */
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  if (chart.length === 0) return branding.colors.primary;
  return chart[index % chart.length] ?? branding.colors.primary;
}

/**
 * Inject all branding CSS variables into the document root
 * Called automatically by ThemeProvider
 */
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors } = branding;

  // Core brand colors
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);

  // Gradient colors
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors
  colors.chart.forEach((color, i) => {
    root.style.setProperty(`--chart-${i + 1}`, color);
  });

  // Background colors (with defaults)
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark = colors.background?.dark ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark = colors.card?.dark ?? "#171717";

  root.style.setProperty("--brand-bg-light", bgLight);
  root.style.setProperty("--brand-bg-dark", bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark", cardDark);

  // Also update the semantic color variables
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);

  // Update background based on current theme
  const isDark = root.classList.contains("dark");
  root.style.setProperty("--background", isDark ? bgDark : bgLight);
  root.style.setProperty("--card", isDark ? cardDark : cardLight);
}

/**
 * Get the MongoDB collection prefix based on company name
 * Converts "SalesGrid" to "salesgrid_", "My Company" to "my_company_"
 */
export function getCollectionPrefix(): string {
  return branding.company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") + "_";
}

/**
 * Get a prefixed collection name
 * Example: getCollectionName("users") returns "salesgrid_users"
 */
export function getCollectionName(baseName: string): string {
  return getCollectionPrefix() + baseName;
}

/**
 * Get a prefixed model name (PascalCase)
 * Example: getModelName("User") returns "SalesGrid_User"
 */
export function getModelName(baseName: string): string {
  const prefix = branding.company.name.replace(/[^a-zA-Z0-9]+/g, "");
  return `${prefix}_${baseName}`;
}
